import React, { useState } from "react";
import { Share2, Facebook, Twitter, MessageCircle, Send, Link2, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { socialLinks, canNativeShare, nativeShare as share, copyText } from "@/lib/share";

const NETWORKS = [
  { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, cls: "bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25" },
  { id: "facebook", label: "Facebook", Icon: Facebook, cls: "bg-[#1877F2]/15 text-[#5ea2ff] hover:bg-[#1877F2]/25" },
  { id: "twitter", label: "X / Twitter", Icon: Twitter, cls: "bg-sand/10 text-sand hover:bg-sand/20" },
  { id: "telegram", label: "Telegram", Icon: Send, href: "telegram", cls: "bg-[#229ED9]/15 text-[#4fc3f7] hover:bg-[#229ED9]/25" },
];

/** Share a visitor photo/video: deep link `/?c=CODE&m=<id>&lang=xx`, OG page `/api/share/media/<id>`. */
export default function ShareMedia({ item, countryName, dark = false, size = "sm", className = "", testid }) {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);
  if (!item) return null;

  const url = `${window.location.origin}/?c=${item.country_code}&m=${item.id}&lang=${lang}`;
  const shareUrl = `${window.location.origin}/api/share/media/${item.id}?lang=${lang}`;
  const text = t("media_share_text").replace("{author}", item.author.name).replace("{country}", countryName || item.country_code);
  const links = socialLinks(shareUrl, text);
  const track = (method) => trackEvent("share", { method, content_type: "media", item_id: item.id, lang });

  const copyLink = async () => {
    track("copy_link");
    await copyText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const btn = size === "sm" ? "w-7 h-7" : "w-9 h-9";
  const tone = dark ? "bg-sand/15 hover:bg-sand/30 text-sand" : "bg-card/90 border border-stoneLine text-ink hover:border-ochre hover:text-ochre backdrop-blur-sm";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label={t("media_share")} title={t("media_share")} onClick={(e) => e.stopPropagation()}
          className={`${btn} rounded-full flex items-center justify-center transition-colors ${tone} ${className}`} data-testid={testid || `share-media-${item.id}`}>
          <Share2 className={size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"} />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-72 p-4 rounded-2xl border-sand/20 bg-ink text-sand z-[80]" onClick={(e) => e.stopPropagation()} data-testid="share-media-popover">
        <div className="text-[10px] uppercase tracking-widest text-sand/60 mb-1">{t("media_share")}</div>
        <div className="text-sm font-semibold truncate mb-3" dir="auto">{item.author.name} · {countryName}</div>
        <div className="grid grid-cols-2 gap-2">
          {NETWORKS.map(({ id, label, Icon, cls }) => (
            <a key={id} href={links[id]} target="_blank" rel="noopener noreferrer" onClick={() => track(id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${cls}`} data-testid={`share-media-${id}`}>
              <Icon className="w-4 h-4" /> {label}
            </a>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-sand/15 bg-sand/5 px-3 py-2">
          <span className="flex-1 truncate text-xs text-sand/60" dir="ltr" data-testid="share-media-url">{url}</span>
          <button onClick={copyLink} aria-label={copied ? t("copied") : "Copy link"} data-testid="share-media-copy"
            className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium ${copied ? "text-oasis" : "text-ochre hover:text-sand"}`}>
            {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
            {copied && t("copied")}
          </button>
        </div>
        {canNativeShare() && (
          <button onClick={() => { track("native"); share({ title: text, text, url: shareUrl }); }} data-testid="share-media-native"
            className="mt-2 w-full py-2 rounded-xl bg-ochre text-white text-sm font-medium hover:bg-ochre/90 transition-colors">
            {t("share")}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
